export type TermsAndConditionsSection = {
  number: number;
  title: string;
  content: string;
};

export const TERMS_AND_CONDITIONS_TITLE = 'End-User License Agreement';

export const TERMS_AND_CONDITIONS_META =
  'Green Line platform and services  ·  Effective July 10, 2026';

export const TERMS_AND_CONDITIONS_INTRODUCTION =
  'This Agreement governs your access to and use of the Green Line software platform and services, including any third-party services you choose to connect. Please read it before using the platform.';

export const TERMS_AND_CONDITIONS_SECTIONS: TermsAndConditionsSection[] = [
  {
    number: 1,
    title: 'Parties and subject',
    content:
      'This End-User License Agreement (“Agreement”) is between Green Line Logistics, LLC (“Green Line,” “we,” “us”) and you, the user accessing our software platform and services (the “Services”). It applies whether you use the Services on your own behalf or on behalf of an organization, in which case you represent that you are authorized to accept this Agreement for that organization.',
  },
  {
    number: 2,
    title: 'Acceptance',
    content:
      'By accessing or using the Services, you accept this Agreement and our Privacy Policy (https://greenlinelog.com/privacy-policy). If you do not accept them, do not use the Services.',
  },
  {
    number: 3,
    title: 'License grant',
    content:
      'Green Line grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for your internal business purposes, in accordance with this Agreement and applicable law.',
  },
  {
    number: 4,
    title: 'Accounts and authorized use',
    content:
      'You are responsible for the accuracy of the information in your account and for all activity that occurs under your credentials. You must be authorized to use the Services for the organization on whose behalf you act, and you may use the Services only for lawful business purposes.',
  },
  {
    number: 5,
    title: 'Third-party services',
    content:
      'The Services may let you connect third-party services (for example, accounting, financial, or operational software). Your use of any third-party service remains subject to that third party’s own terms and policies. You represent that you are authorized to connect any service and the associated account, and Green Line is not responsible for third-party services themselves or their availability.',
  },
  {
    number: 6,
    title: 'Data and privacy',
    content:
      'Our handling of information, including data retrieved from services you connect, is described in our Privacy Policy (https://greenlinelog.com/privacy-policy), which is incorporated into this Agreement by reference.',
  },
  {
    number: 7,
    title: 'Restrictions',
    content:
      'You will not: (a) reverse engineer, decompile, or attempt to derive source code from the Services; (b) sublicense, resell, or distribute the Services to unauthorized third parties; (c) use the Services to access data you are not authorized to access; (d) interfere with or circumvent the Services’ security features; or (e) use the Services in violation of applicable law or any applicable third-party terms.',
  },
  {
    number: 8,
    title: 'Intellectual property',
    content:
      'The Services and all related software and documentation are owned by Green Line or its licensors. Except for the limited license in Section 3, no rights are transferred to you.',
  },
  {
    number: 9,
    title: 'Security and credentials',
    content:
      'You are responsible for safeguarding your credentials and any authorization tokens issued through your connections, and for promptly notifying Green Line of any suspected unauthorized access or credential compromise.',
  },
  {
    number: 10,
    title: 'Support and updates',
    content:
      'Green Line may provide maintenance, updates, and support at its discretion and may modify or discontinue features of the Services. Updates may be applied automatically.',
  },
  {
    number: 11,
    title: 'Term, termination, and disconnection',
    content:
      'This Agreement is effective until terminated. You may stop using the Services and disconnect any connected third-party service at any time from within the platform or from that third party’s own settings. Green Line may suspend or terminate access for breach of this Agreement. On disconnection or termination, we stop retrieving data from the affected service and handle stored data as described in the Privacy Policy.',
  },
  {
    number: 12,
    title: 'Disclaimer of warranties',
    content:
      'TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE,” AND GREEN LINE DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. GREEN LINE DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT DISPLAYED DATA WILL BE COMPLETE OR ACCURATE.',
  },
  {
    number: 13,
    title: 'Limitation of liability',
    content:
      'TO THE MAXIMUM EXTENT PERMITTED BY LAW, GREEN LINE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS OR LOST DATA, ARISING FROM OR RELATED TO THE SERVICES. GREEN LINE’S TOTAL LIABILITY ARISING FROM OR RELATED TO THE SERVICES WILL NOT EXCEED THE GREATER OF THE AMOUNTS YOU PAID TO GREEN LINE IN THE PRIOR TWELVE MONTHS OR ONE THOUSAND U.S. DOLLARS ($1,000).',
  },
  {
    number: 14,
    title: 'Indemnification',
    content:
      'You will indemnify and hold Green Line harmless from claims, losses, and expenses arising from your misuse of the Services, your connection of any service or account you were not authorized to access, or your breach of this Agreement.',
  },
  {
    number: 15,
    title: 'Governing law and venue',
    content:
      'This Agreement is governed by the laws of the State of Illinois, without regard to its conflict-of-laws rules. The exclusive venue for any dispute is the state and federal courts located in Cook County, Illinois, and the parties consent to their jurisdiction.',
  },
  {
    number: 16,
    title: 'Changes to this Agreement',
    content:
      'Green Line may revise this Agreement from time to time. Material changes will be posted on this page with a revised effective date. Continued use of the Services after an update constitutes acceptance.',
  },
];
